import { customerPortalApi } from './apiInstances';

/**
 * WP-13 slice 4 — the customer's maintenance schedule for one vehicle.
 *
 * The server decides everything here. The client does not compute a due date, a
 * status, or a "days away" figure from the parts it was given — if it did, there
 * would be two implementations of the rule that decides whether someone's brake
 * fluid is overdue, and they would drift. `reason` arrives as a finished
 * sentence for the same reason.
 */

export type DueStatus =
  | 'overdue'
  | 'due_soon'
  | 'upcoming'
  | 'never_performed'
  | 'not_enough_data'
  | 'no_interval'
  | 'needs_review';

export interface ServiceDueRow {
  template_id: number | null;
  override_id: number | null;
  name: string;
  category: string | null;
  interval_miles: number | null;
  interval_months: number | null;
  provenance: 'shop_default' | 'staff_reviewed' | 'ai_suggested' | 'manufacturer';
  is_authoritative: boolean;
  status: DueStatus;
  reason: string;
  last_performed_at: string | null;
  last_performed_mileage: number | null;
  matched_by: 'keyword' | null;
  due_at_miles: number | null;
  miles_remaining: number | null;
  due_at_date: string | null;
  days_remaining: number | null;
  driven_by: 'miles' | 'time' | null;
  mileage_log_has_rollback: boolean;
}

export interface ServiceTable {
  vehicle: {
    id: number;
    year?: number;
    make?: string;
    model?: string;
    drive_type?: string | null;
    license_plate?: string | null;
  };
  current_mileage: number | null;
  mileage_log_has_rollback: boolean;
  services: ServiceDueRow[];
}

export async function fetchServiceTable(vehicleId: number): Promise<ServiceTable> {
  const res = await customerPortalApi.get(`/customer-portal/vehicles/${vehicleId}/service-table`);
  return res.data;
}

/**
 * Presentation for each status.
 *
 * The four non-answer statuses all render in a NEUTRAL colour and say plainly
 * what is missing. None of them borrows the green of "you're fine" or the red of
 * "you're overdue" — a customer glancing at a colour must not read "we don't
 * know when your brake fluid is due" as either.
 */
export const STATUS_PRESENTATION: Record<
  DueStatus,
  { label: string; color: string; tone: 'answer' | 'unknown' }
> = {
  overdue: { label: 'Overdue', color: 'red', tone: 'answer' },
  due_soon: { label: 'Due soon', color: 'orange', tone: 'answer' },
  upcoming: { label: 'Upcoming', color: 'green', tone: 'answer' },
  never_performed: { label: 'No record', color: 'default', tone: 'unknown' },
  not_enough_data: { label: 'Not enough data', color: 'default', tone: 'unknown' },
  no_interval: { label: 'No interval set', color: 'default', tone: 'unknown' },
  needs_review: { label: 'Awaiting shop review', color: 'default', tone: 'unknown' },
};

/** Sort so the things a customer must act on are at the top. */
const STATUS_ORDER: DueStatus[] = [
  'overdue',
  'due_soon',
  'upcoming',
  'never_performed',
  'not_enough_data',
  'no_interval',
  'needs_review',
];

export function sortByUrgency(rows: ServiceDueRow[]): ServiceDueRow[] {
  return [...rows].sort((a, b) => {
    const d = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
    if (d !== 0) return d;
    // Within a status, closest to due first. Rows without a figure sort last
    // rather than sorting as 0, which would put them among the most urgent.
    const am = a.miles_remaining ?? Number.POSITIVE_INFINITY;
    const bm = b.miles_remaining ?? Number.POSITIVE_INFINITY;
    if (am !== bm) return am - bm;
    return a.name.localeCompare(b.name);
  });
}
